import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

import IntegrationIcon from '@/assets/icons/integration.svg?react';
import PencilRulerIcon from '@/assets/icons/pencilNruler.svg?react';

import ShippingIcon from "@/assets/features/shippingActive.png";
import gridImage from "@/assets/gridImages/shipping/gridImage.png";
import gridImage2 from "@/assets/gridImages/shipping/gridImage2.png";
import gridImage3 from "@/assets/gridImages/shipping/gridImage3.png";
import gridImage4 from "@/assets/gridImages/shipping/gridImage4.png";
import gridImage5 from "@/assets/gridImages/shipping/gridImage5.png";

const FirstCard = () => (
  <div className="tw-w-full tw-h-full tw-bg-[#1F2A27] tw-rounded-xl tw-p-6 tw-text-white tw-font-sans tw-flex tw-flex-col tw-gap-4 tw-justify-center">
  <p className="tw-text-2xl tw-font-semibold">Reduced financial risk</p>
  <h2 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-tracking-wide tw-break-words tw-whitespace-normal">
    We’ve got you from checkout to delivery.
  </h2>
  <p className="tw-text-base tw-text-gray-300">
    Protega takes on all liability, including replacement and shipping costs
  </p>
</div>

);

const SecondCard = () => (
  <div className="tw-w-full tw-h-full tw-bg-[#1F2A27] tw-rounded-xl tw-p-4 tw-text-white tw-font-sans tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1">
    <h2 className="tw-text-7xl tw-font-extrabold">52%</h2>
    <p className="tw-text-3xl tw-text-gray-300 tw-font-medium tw-text-center">
      Average Attach Rate
    </p>
  </div>
);

const ThirdCard = () => (
  <div className="tw-w-full tw-h-full tw-bg-[#1F2A27] tw-rounded-xl tw-p-4 tw-text-white tw-font-sans tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
    <div className="tw-text-3xl tw-text-gray-400">
      <PencilRulerIcon />
    </div>
    <p className="tw-text-3xl tw-text-gray-300 tw-font-medium tw-text-center">
      Co-marketing <br /> Support
    </p>
  </div>
);

const FourthCard = () => (
  <div className="tw-w-full tw-h-full tw-bg-[#1F2A27] tw-rounded-xl tw-p-4 tw-text-white tw-font-sans tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
    <div className="tw-text-3xl tw-text-gray-400">
      <IntegrationIcon />
    </div>

    <p className="tw-text-3xl tw-text-gray-300 tw-font-medium tw-text-center">
      Easy integrations
    </p>
  </div>
);

const FifthCard = () => (
  <div className="tw-w-full tw-h-full tw-bg-[#1F2A27] tw-rounded-xl tw-p-4 tw-text-white tw-font-sans tw-flex tw-flex-col tw-items-start tw-justify-center">
    <h2 className="tw-text-7xl tw-font-extrabold">24/7</h2>
    <p className="tw-text-xl tw-text-gray-300 tw-font-medium tw-pt-4">
      Virtual claim filing reduces merchants' operational costs
    </p>
  </div>
);

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const flip1Ref = useRef<HTMLDivElement>(null);
  const flip5Ref = useRef<HTMLDivElement>(null);

  const flip2Ref = useRef<HTMLDivElement>(null);
  const flip3Ref = useRef<HTMLDivElement>(null);
  const flip4Ref = useRef<HTMLDivElement>(null);


  useEffect(() => {
  if (!flip1Ref.current || !flip2Ref.current || !flip3Ref.current || !flip4Ref.current || !flip5Ref.current) {
    return;
  }

  // timing constants
  const initialDelay = 6;   // wait before group 1 flips
  const flipDuration = 1;   // how long the flip itself takes
  const hold1 = 4;          // how long group 1 stays flipped
  const between = 3;        // gap between group 1 back-flip and group 2 flip
  const hold2 = 4;          // how long group 2 stays flipped

  const t1 = initialDelay;
  const t2 = t1 + flipDuration + hold1;
  const t3 = t2 + flipDuration + between;
  const t4 = t3 + flipDuration + hold2;

  // build master timeline
  const master = gsap.timeline({ repeat: -1, repeatDelay: 0, defaults: { ease: 'power2.inOut' } });

  // 1) Group 1 flip to 180° at t1
  master.to(
    [flip1Ref.current, flip5Ref.current],
    { rotationY: 180, duration: flipDuration },
    t1
  );

  // 2) Group 1 flip back to 0° at t2
  master.to(
    [flip1Ref.current, flip5Ref.current],
    { rotationY: 0, duration: flipDuration },
    t2
  );

  // 3) Group 2 flip to 180° at t3
  master.to(
    [flip2Ref.current, flip3Ref.current, flip4Ref.current],
    { rotationY: 180, duration: flipDuration },
    t3
  );

  // 4) Group 2 flip back to 0° at t4
  master.to(
    [flip2Ref.current, flip3Ref.current, flip4Ref.current],
    { rotationY: 0, duration: flipDuration },
    t4
  );

  // cleanup
  return () => {
    master.kill();
  };
}, []);


  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.43, 0.13, 0.23, 0.96]
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="tw-w-full tw-h-5/6 xl:tw-h-screen tw-flex tw-flex-col xl:tw-flex-row tw-gap-6 tw-overflow-hidden tw-px-6"
    >
      {/* LEFT PANEL */}
      <motion.div
        className="tw-bg-[#04f2b4] tw-p-8 md:tw-p-12 xl:tw-p-16 tw-w-full xl:tw-w-[45%] tw-flex tw-flex-col tw-justify-center tw-items-start tw-rounded-2xl tw-h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="tw-flex tw-items-center tw-mb-6">
          <img src={ShippingIcon} alt="Protega Logo" className="tw-h-12" />
          <div className="tw-flex tw-flex-col tw-ml-2">
            <div className="tw-flex tw-items-center tw-text-md tw-font-medium tw-text-black tw-gap-0.5">
              Protega
            </div>
            <span className="tw-text-lg tw-font-bold tw-text-black">Product Protection</span>
          </div>
        </div>
        <h1
          ref={titleRef}
          className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-black tw-leading-none   tw-tracking-wider tw-w-[95%] lg:tw-w-[70%] xl:tw-w-full"
        >
          Ease delivery stress with Protega Shipping Protection.
        </h1>
      </motion.div>

      {/* RIGHT SIDE */}
      <div className="tw-hidden xl:tw-flex tw-w-full xl:tw-w-[55%] tw-flex-col tw-gap-6 tw-rounded-tr-3xl tw-rounded-br-3xl tw-bg-black tw-h-full">
        {/* TOP GRID */}
        <motion.div
          className="tw-grid tw-grid-cols-2 tw-grid-rows-2 tw-gap-6 tw-flex-1 tw-overflow-hidden"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {/* 1. Tall hero with flip */}
          <motion.div className="tw-row-span-2 tw-overflow-hidden" variants={item}>
            <motion.div
              ref={flip1Ref}
              className="tw-w-full tw-h-full tw-rounded-lg tw-relative"
              initial={false}
              transition={{ duration: 1 }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <motion.img
                src={gridImage}
                alt="Hero girl"
                className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                initial={{ filter: 'grayscale(0)' }}
                animate={{ filter: 'grayscale(1)' }}
                transition={{ delay: 3, duration: 1 }}
                style={{ backfaceVisibility: 'hidden' }}
              />
              <div
                className="tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-lg"
                style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
              >
                <FirstCard />
              </div>
            </motion.div>
          </motion.div>

          {/* 2. Top-right */}
          <motion.div className="tw-overflow-hidden" variants={item}>
    <motion.div
      ref={flip2Ref}
      className="tw-w-full tw-h-full tw-rounded-lg tw-relative"
      initial={false}
      transition={{ duration: 1 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* front face */}
      <motion.img
        src={gridImage2}
        alt="Headphones"
        className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
        initial={{ filter: 'grayscale(0)' }}
        animate={{ filter: 'grayscale(1)' }}
        transition={{ delay: 3, duration: 1 }}
        style={{ backfaceVisibility: 'hidden' }}
      />
      {/* back face */}
      <div
        className="tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-lg"
        style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
      >
        <SecondCard />
      </div>
    </motion.div>
  </motion.div>

  {/* 3. Mid‑right with flip3Ref */}
  <motion.div className="tw-overflow-hidden" variants={item}>
    <motion.div
      ref={flip3Ref}
      className="tw-w-full tw-h-full tw-rounded-lg tw-relative"
      initial={false}
      transition={{ duration: 1 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <motion.img
        src={gridImage3}
        alt="Smiling girl"
        className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
        initial={{ filter: 'grayscale(0)' }}
        animate={{ filter: 'grayscale(1)' }}
        transition={{ delay: 3, duration: 1 }}
        style={{ backfaceVisibility: 'hidden' }}
      />
      <div
        className="tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-lg"
        style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
      >
        <ThirdCard />
      </div>
    </motion.div>
  </motion.div>
</motion.div>

{/* BOTTOM ROW */}
<motion.div className="tw-flex tw-gap-6 tw-h-[30%] tw-mr-6" variants={container} initial="hidden" animate="show">
  {/* 4. Bottom‑left with flip4Ref */}
  <motion.div className="tw-overflow-hidden" variants={item} style={{ flex: '1 0 40%' }}>
    <motion.div
      ref={flip4Ref}
      className="tw-w-full tw-h-full tw-rounded-lg tw-relative"
      initial={false}
      transition={{ duration: 1 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <motion.img
        src={gridImage4}
        alt="Watch"
        className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
        initial={{ filter: 'grayscale(0)' }}
        animate={{ filter: 'grayscale(1)' }}
        transition={{ delay: 3, duration: 1 }}
        style={{ backfaceVisibility: 'hidden' }}
      />
      <div
        className="tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-lg"
        style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
      >
        <FourthCard />
      </div>
    </motion.div>
  </motion.div>

          {/* 5. Bottom-right with flip */}
          <motion.div className="tw-overflow-hidden" variants={item} style={{ flex: '1 0 60%' }}>
            <motion.div
              className="tw-w-full tw-h-full tw-rounded-lg tw-relative"
              ref={flip5Ref}
              initial={false}
              transition={{ duration: 1 }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <motion.img
                src={gridImage5}
                alt="Phone"
                className="tw-absolute tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                initial={{ filter: 'grayscale(0)' }}
                animate={{ filter: 'grayscale(1)' }}
                transition={{ delay: 3, duration: 1 }}
                style={{ backfaceVisibility: 'hidden' }}
              />
              <div
                className="tw-absolute tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-lg"
                style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
              >
                <FifthCard />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;